/* eslint max-classes-per-file: 0 */

import {
  Publication as Data,
  PublicationCollection as Collection,
  PublicationParticipant,
  PublicationParticipantCollection,
} from '@/typings/api/Publication'
import { Participant } from '@/typings/api/Participant'

import { Fabric } from '@/store/createCrudModule'
import { PublicationsAdapter } from './PublicationsAdapter'
import { unionBy } from 'lodash'
import { ParticipantsAdapter } from '@/views/Participants/store/ParticipantsAdapter'


export const fabric = Fabric<Data, Collection>()

type S = PublicationsState
type G = PublicationsGetters
type M = PublicationsMutations
type A = PublicationsActions


export class PublicationsState extends fabric.State {
  public participants: PublicationParticipant[] | null = [];
}

export class PublicationsGetters extends fabric.Getters<S> {
}

export class PublicationsMutations extends fabric.Mutations<S> {
  public setRedirect(value: boolean) {
    if (!this.state.single) return
    this.state.single.is_redirect_to_self = value
  }

  public setParticipants(payload?: PublicationParticipant[]) {
    this.state.participants = payload || null
  }

  public addParticipants(payload?: PublicationParticipant[]) {
    if (!this.state.participants || !payload) return
    this.state.participants = unionBy(this.state.participants, payload, 'id')
  }

  public removeParticipant(payload?: PublicationParticipant) {
    if (!this.state.participants || !payload) return

    const value = this.state.participants.slice()
    const index = value.findIndex(({ id }) => id === payload.id)
    if (index >= 0) {
      value.splice(index, 1)
      this.state.participants = value
    }
  }

  public onParticipantUsersCountChanged({ payload, count }: { payload: PublicationParticipant['id']; count: number }) {
    const p = this.state.participants?.find(({ id }) => id === payload)
    if (p) {
      p.users_count = count
    }
  }
}

export class PublicationsActions extends fabric.Actions<S, G, M, A> {
  protected readonly adapter = new PublicationsAdapter();

  protected readonly participantsAdapter = new ParticipantsAdapter();

  protected readonly idName = 'id';

  public archive(options: { id: Data['id'] }) {
    return this.adapter.api.archive(options.id)
      .then(() => this.actions.getOne(options))
  }

  public unarchive(options: { id: Data['id'] }) {
    return this.adapter.api.unarchive(options.id)
      .then(() => this.actions.getOne(options))
  }

  public getDefaultRedirect() {
    return this.adapter.api.getDefaultRedirect()
  }

  public async redirect(options: { id: Data['id'] }) {
    const params = { is_redirect: !this.state.single?.is_redirect_to_self }
    const { data } = await this.adapter.api.redirect(options.id, params)
    this.mutations.setRedirect(data?.is_redirect_to_self)
  }

  public copy(options: Data) {
    return this.adapter.api.copy(options.id)
  }

  public async getParticipants(options: Data) {
    const data = await this.adapter.api.getParticipants<PublicationParticipantCollection>(options.id)
    this.mutations.setParticipants(data.data)

    return this.state.participants
  }

  public async addParticipant({ options, participant }: { options: Data; participant: PublicationParticipant }) {
    if (!participant) return

    await this.adapter.api.addParticipants(options.id, { participants_id: participant.id })
    this.mutations.addParticipants([participant])
  }

  public async removeParticipant({ options, participant }: {options: Data; participant: PublicationParticipant}) {
    if (!participant) return

    await this.adapter.api.removeParticipants(options.id, { participants_id: participant.id })
    this.mutations.removeParticipant(participant)
  }

  public exportParticipantUsers(options: { id: Participant['id'] }) {
    const link = this.participantsAdapter.api.export(options.id)
    window.open(link, '_blank')
  }
}
